import { formatServiceMonth, formatComparisonValue, formatComparisonPercent } from "./comparison.mjs";
import React from 'react';
import { Col, Row } from "antd";
import { useRequest } from 'ahooks';
import { Swiper } from "antd-mobile";
import { api } from "@redux/api";
import basicStyle from "@assets/styles/constants.js";
import DashboardCard from './DashbordCard';
import { useAuth } from '@/hooks/use-auth';
import { useSelector } from 'react-redux';
import { chartIdDashBordMappings } from '@/containers/PdfReport/template/dashboard/dashboard-chart';
import "./DashboardCards.css";

const NoPermissionCard = () => {
    return <div style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', borderRadius: '8px' }}>No permission</div>
}

const DashboardCards = () => {
    const { rowStyle } = basicStyle;
    const gasPermission = useAuth('gas', 1);
    const electricityPermission = useAuth('electricity', 1);
    const facilityId = useSelector(state => state?.Facility?.selected?.Id);
    const view = useSelector((state) => state.App.view);
    const isMobile = view === "MobileView";

    // 分别获取电力和燃气数据
    const { data: electricityData } = useRequest(
        () => api.getElecComparision(),
        {
            ready: electricityPermission,
            cacheKey: `electricity-comparison-data-${facilityId}`,
            refreshDeps: [facilityId],
            staleTime: 1 * 60 * 1000, // 1分钟缓存
        }
    );

    const { data: gasData } = useRequest(
        () => api.getGasComparision(),
        {
            ready: gasPermission,
            cacheKey: `gas-comparison-data-${facilityId}`,
            refreshDeps: [facilityId],
            staleTime: 1 * 60 * 1000, // 1分钟缓存
        }
    );

    // 从API数据中提取电力和燃气数据
    const electricityResponse = electricityData?.data;
    const gasResponse = gasData?.data;

    const colCardLayout = {
        xxl: 6,
        xl: 6,
        lg: 12,
        md: 12,
        sm: 12,
        xs: 24,
        className: 'my-4',
        style: {
            padding: '4px',
            width: '100%',
            minHeight: 200
        }
    };

    const electricityConsumptionCard = electricityPermission ? <DashboardCard
        title="Electricity Monthly Consumption"
        value={formatComparisonValue(electricityResponse?.electricity?.latest_consumption)}
        unit="kWh"
        percent={formatComparisonPercent(electricityResponse?.electricity?.consumption_diff)}
        startDate={formatServiceMonth(electricityResponse?.electricity?.latest_service_month)}
        endDate={formatServiceMonth(electricityResponse?.electricity?.last_service_month)}
        isIncreased={Number(electricityResponse?.electricity?.consumption_diff) > 0}
        variant="blue"
    /> : <NoPermissionCard />;

    const electricityCostCard = electricityPermission ? <DashboardCard
        title="Electricity Monthly Cost"
        value={formatComparisonValue(electricityResponse?.electricity?.latest_cost, true)}
        unit=""
        percent={formatComparisonPercent(electricityResponse?.electricity?.cost_diff)}
        startDate={formatServiceMonth(electricityResponse?.electricity?.latest_service_month)}
        endDate={formatServiceMonth(electricityResponse?.electricity?.last_service_month)}
        isIncreased={Number(electricityResponse?.electricity?.cost_diff) > 0}
        variant="green"
    /> : <NoPermissionCard />;

    const gasConsumptionCard = gasPermission ? <DashboardCard
        title="Gas Monthly Consumption"
        value={formatComparisonValue(gasResponse?.gas?.latest_consumption)}
        unit="MMBtu"
        percent={formatComparisonPercent(gasResponse?.gas?.consumption_diff)}
        startDate={formatServiceMonth(gasResponse?.gas?.latest_service_month)}
        endDate={formatServiceMonth(gasResponse?.gas?.last_service_month)}
        isIncreased={Number(gasResponse?.gas?.consumption_diff) > 0}
        variant="purple"
    /> : <NoPermissionCard />;

    const gasCostCard = gasPermission ? <DashboardCard
        title="Gas Monthly Cost"
        value={formatComparisonValue(gasResponse?.gas?.latest_cost, true)}
        unit=""
        percent={formatComparisonPercent(gasResponse?.gas?.cost_diff)}
        startDate={formatServiceMonth(gasResponse?.gas?.latest_service_month)}
        endDate={formatServiceMonth(gasResponse?.gas?.last_service_month)}
        isIncreased={Number(gasResponse?.gas?.cost_diff) > 0}
        variant="orange"
    /> : <NoPermissionCard />;

    const renderMobileCardCarousel = (cards, className) => (
        <Col xs={24} className={className} style={{ padding: '4px', width: '100%', minHeight: 200 }}>
            <div className="dashboard-card-carousel">
                <Swiper
                    loop={false}
                    className="dashboard-card-carousel-swiper"
                    indicator={(total, current) => (
                        <div className="dashboard-card-carousel-dots">
                            {Array.from({ length: total }, (_, index) => (
                                <span
                                    key={index}
                                    className={`dashboard-card-carousel-dot${index === current ? " active" : ""}`}
                                />
                            ))}
                        </div>
                    )}
                >
                    {cards.map((card, index) => (
                        <Swiper.Item key={index}>
                            <div className="dashboard-card-carousel-item">
                                {card}
                            </div>
                        </Swiper.Item>
                    ))}
                </Swiper>
            </div>
        </Col>
    );

    if (isMobile) {
        return (
            <Row style={rowStyle} gutter={8} justify="start" className={''}>
                {renderMobileCardCarousel(
                    [electricityConsumptionCard, electricityCostCard],
                    chartIdDashBordMappings.cards[0]
                )}
                {renderMobileCardCarousel(
                    [gasConsumptionCard, gasCostCard],
                    chartIdDashBordMappings.cards[2]
                )}
            </Row>
        );
    }

    return (
        <Row style={rowStyle} gutter={8} justify="start" className={''}>
            <Col {...colCardLayout} className={chartIdDashBordMappings.cards[0]}>
                {electricityConsumptionCard}
            </Col>
            
            <Col {...colCardLayout} className={chartIdDashBordMappings.cards[1]}>
                {electricityCostCard}
            </Col>
            
            <Col {...colCardLayout} className={chartIdDashBordMappings.cards[2]}>
                {gasConsumptionCard}
            </Col>
            
            <Col {...colCardLayout} className={chartIdDashBordMappings.cards[3]}>
                {gasCostCard}
            </Col>
        </Row>
    );
};

export default DashboardCards;
